/**
 * NetworkConnectionManager
 * ─────────────────────────────────────────────────────────────────────────────
 * Centralized, singleton network-loss handler for the AV chat application.
 *
 * Detection layers implemented:
 *   L1 – Browser online/offline events   (window "online" / "offline")
 *   L2 – Socket.IO connection state      (disconnect / connect_error / reconnect)
 *   L3 – WebRTC connection state         (connectionState change per peer)
 *   L4 – ICE connection state            (iceConnectionState change per peer)
 *   L5 – Media track readyState          (track.onended)
 *
 * Key design rules:
 *   • Single source of truth — one instance drives all UI and cleanup.
 *   • No duplicate timers — reconnect-timeout is started once and guarded.
 *   • No duplicate listeners — attach/detach helpers prevent double-wiring.
 *   • No memory leaks — cleanup() removes every listener and timer.
 *   • Does NOT break recording, messaging, camera/mic, participant or timer flows.
 */

import store from "../store/store";
import { setNetworkStatus } from "../store/actions";

// ─── Public status codes (consumed by Redux + UI) ────────────────────────────
export const NETWORK_STATUS = {
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
  FAILED: "failed",
};

// ─── Configurable timeouts (ms) ──────────────────────────────────────────────
const RECONNECT_TIMEOUT_MS = 30_000; // 30 s before declaring hard failure
// SUCCESS_BANNER_HIDE_MS removed — the NetworkStatusBanner component owns that timer

// ─── Singleton state ─────────────────────────────────────────────────────────
let _socket = null;               // Socket.IO instance, set via init()
let _getPeers = null;             // () => peers map, set via init()
let _onHardFailure = null;        // callback for redirect / cleanup, set via init()

let _reconnectTimer = null;
let _successBannerTimer = null;
let _isReconnecting = false;
let _hardFailed = false;

// Store the peer connection-state listeners so we can remove them later
// Map<socketId, { connStateHandler, iceStateHandler }>
const _peerListeners = new Map();

// ─── Helpers ─────────────────────────────────────────────────────────────────

const dispatch = (status) => {
  store.dispatch(setNetworkStatus(status));
};

const clearReconnectTimer = () => {
  if (_reconnectTimer) {
    clearTimeout(_reconnectTimer);
    _reconnectTimer = null;
  }
};

const startReconnectTimer = () => {
  // Only one timer at a time
  if (_reconnectTimer) return;

  _reconnectTimer = setTimeout(() => {
    if (_isReconnecting) {
      console.warn("[NetworkManager] Reconnect timeout — hard failure.");
      _hardFailed = true;
      dispatch(NETWORK_STATUS.FAILED);
      _onHardFailure && _onHardFailure();
    }
  }, RECONNECT_TIMEOUT_MS);
};

const enterReconnecting = (reason) => {
  if (_hardFailed) return;
  if (_isReconnecting) return; // already in reconnecting — don't re-trigger

  console.log(`[NetworkManager] Entering reconnecting state. Reason: ${reason}`);
  _isReconnecting = true;

  // Cancel any pending "reconnected" success banner
  if (_successBannerTimer) {
    clearTimeout(_successBannerTimer);
    _successBannerTimer = null;
  }

  dispatch(NETWORK_STATUS.RECONNECTING);
  startReconnectTimer();
};

const enterConnected = (reason) => {
  if (_hardFailed) return;
  if (!_isReconnecting) return; // wasn't reconnecting — nothing to do

  console.log(`[NetworkManager] Connection restored. Reason: ${reason}`);
  _isReconnecting = false;
  clearReconnectTimer();

  // Dispatch CONNECTED immediately.
  // The NetworkStatusBanner component tracks the RECONNECTING → CONNECTED
  // transition itself and handles the 3-second "Reconnected ✓" flash,
  // so we do NOT need to dispatch RECONNECTING again here.
  dispatch(NETWORK_STATUS.CONNECTED);
};

// ─── Layer 1 — Browser network events ────────────────────────────────────────

const _handleOffline = () => {
  console.log("[NetworkManager] L1: browser offline event");
  enterReconnecting("browser-offline");
};

const _handleOnline = () => {
  console.log("[NetworkManager] L1: browser online event");
  // browser says we're back — but we wait for socket reconnect to confirm
  // (navigator.onLine is unreliable on Safari; socket reconnect is the truth)
};

// ─── Layer 2 — Socket.IO events ──────────────────────────────────────────────

const _attachSocketListeners = (socket) => {
  socket.on("disconnect", _onSocketDisconnect);
  socket.on("connect_error", _onSocketConnectError);
  socket.on("reconnect_attempt", _onSocketReconnectAttempt);
  socket.on("reconnect", _onSocketReconnected);
  socket.on("connect", _onSocketConnect);
  socket.on("reconnect_failed", _onSocketReconnectFailed);
};

const _detachSocketListeners = (socket) => {
  if (!socket) return;
  socket.off("disconnect", _onSocketDisconnect);
  socket.off("connect_error", _onSocketConnectError);
  socket.off("reconnect_attempt", _onSocketReconnectAttempt);
  socket.off("reconnect", _onSocketReconnected);
  socket.off("connect", _onSocketConnect);
  socket.off("reconnect_failed", _onSocketReconnectFailed);
};

const _onSocketDisconnect = (reason) => {
  console.log(`[NetworkManager] L2: socket disconnect (${reason})`);
  // "io server disconnect" means the server intentionally kicked us — don't reconnect
  if (reason === "io server disconnect") return;
  enterReconnecting(`socket-disconnect:${reason}`);
};

const _onSocketConnectError = (err) => {
  console.log("[NetworkManager] L2: socket connect_error", err?.message);
  enterReconnecting("socket-connect-error");
};

const _onSocketReconnectAttempt = (attempt) => {
  console.log(`[NetworkManager] L2: socket reconnect attempt #${attempt}`);
  // Ensure we're in reconnecting state (in case it wasn't triggered yet)
  enterReconnecting("socket-reconnect-attempt");
};

const _onSocketReconnected = (attempt) => {
  console.log(`[NetworkManager] L2: socket reconnected after ${attempt} attempt(s)`);
  enterConnected("socket-reconnect");
};

const _onSocketConnect = () => {
  console.log("[NetworkManager] L2: socket connect");
  if (_isReconnecting) {
    enterConnected("socket-connect");
  }
};

const _onSocketReconnectFailed = () => {
  console.warn("[NetworkManager] L2: socket reconnect_failed — all attempts exhausted");
  if (!_hardFailed) {
    _hardFailed = true;
    clearReconnectTimer();
    dispatch(NETWORK_STATUS.FAILED);
    _onHardFailure && _onHardFailure();
  }
};

// ─── Layer 3 & 4 — WebRTC / ICE state per peer ───────────────────────────────

/**
 * Call this when a new peer is created to wire up connection-state monitoring.
 * safe to call multiple times with the same socketId — it detaches first.
 */
export const monitorPeer = (socketId, peer) => {
  // Detach old listeners for this socket if they exist
  unmonitorPeer(socketId);

  const pc = peer._pc; // simple-peer exposes the underlying RTCPeerConnection as ._pc
  if (!pc) return;

  const connStateHandler = () => {
    const state = pc.connectionState;
    console.log(`[NetworkManager] L3: peer ${socketId} connectionState → ${state}`);
    if (state === "disconnected" || state === "failed") {
      enterReconnecting(`webrtc-conn-${state}:${socketId}`);
    } else if (state === "connected") {
      enterConnected(`webrtc-conn-connected:${socketId}`);
    }
  };

  const iceStateHandler = () => {
    const state = pc.iceConnectionState;
    console.log(`[NetworkManager] L4: peer ${socketId} iceConnectionState → ${state}`);
    if (state === "disconnected" || state === "failed") {
      enterReconnecting(`ice-${state}:${socketId}`);
    } else if (state === "connected" || state === "completed") {
      enterConnected(`ice-${state}:${socketId}`);
    }
  };

  pc.addEventListener("connectionstatechange", connStateHandler);
  pc.addEventListener("iceconnectionstatechange", iceStateHandler);

  _peerListeners.set(socketId, { connStateHandler, iceStateHandler, pc });
};

/**
 * Remove listeners for a peer that has been destroyed / disconnected.
 */
export const unmonitorPeer = (socketId) => {
  const entry = _peerListeners.get(socketId);
  if (!entry) return;
  const { connStateHandler, iceStateHandler, pc } = entry;
  try {
    pc.removeEventListener("connectionstatechange", connStateHandler);
    pc.removeEventListener("iceconnectionstatechange", iceStateHandler);
  } catch (_) {}
  _peerListeners.delete(socketId);
};

// ─── Layer 5 — Media track state ─────────────────────────────────────────────

/**
 * Monitor a remote stream's tracks. If a track ends unexpectedly (not due to
 * a graceful peer removal) we flag it as a potential network issue.
 */
export const monitorRemoteStream = (stream, socketId) => {
  stream.getTracks().forEach((track) => {
    track.addEventListener("ended", () => {
      console.log(`[NetworkManager] L5: track ended (${track.kind}) for peer ${socketId}`);
      // Only treat as network issue if we haven't already cleaned up this peer
      const peers = _getPeers ? _getPeers() : {};
      if (peers[socketId]) {
        enterReconnecting(`track-ended:${track.kind}:${socketId}`);
      }
    });
  });
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Initialize the manager. Call once when entering the room.
 *
 * @param {object} socket        – Socket.IO socket instance
 * @param {function} getPeers    – () => peers map (so we get latest reference)
 * @param {function} onHardFailure – called when reconnect timeout expires
 */
export const init = (socket, getPeers, onHardFailure) => {
  // Defensive: clean up any previous session
  cleanup();

  _socket = socket;
  _getPeers = getPeers;
  _onHardFailure = onHardFailure;
  _isReconnecting = false;
  _hardFailed = false;

  // Layer 1
  window.addEventListener("offline", _handleOffline);
  window.addEventListener("online", _handleOnline);

  // Layer 2
  _attachSocketListeners(socket);

  // Set initial status
  dispatch(NETWORK_STATUS.CONNECTED);

  console.log("[NetworkManager] Initialized.");
};

/**
 * Tear down everything. Call when leaving the room.
 */
export const cleanup = () => {
  // Layer 1
  window.removeEventListener("offline", _handleOffline);
  window.removeEventListener("online", _handleOnline);

  // Layer 2
  _detachSocketListeners(_socket);

  // Layer 3 & 4 — all peer listeners
  _peerListeners.forEach(({ connStateHandler, iceStateHandler, pc }) => {
    try {
      pc.removeEventListener("connectionstatechange", connStateHandler);
      pc.removeEventListener("iceconnectionstatechange", iceStateHandler);
    } catch (_) {}
  });
  _peerListeners.clear();

  // Timers
  clearReconnectTimer();
  if (_successBannerTimer) {
    clearTimeout(_successBannerTimer);
    _successBannerTimer = null;
  }

  _socket = null;
  _getPeers = null;
  _onHardFailure = null;
  _isReconnecting = false;
  _hardFailed = false;

  console.log("[NetworkManager] Cleaned up.");
};

/**
 * Returns true if we are currently in a degraded/reconnecting/failed state.
 * Useful for guarding send() calls in NewMessage and broadcastCameraState.
 */
export const isDisconnected = () => {
  const status = store.getState().networkStatus;
  return status === NETWORK_STATUS.RECONNECTING || status === NETWORK_STATUS.FAILED;
};